// Google Analytics 4 (GA4) Tracking Utility - Data Attribute Based
import { captureMessage } from '@sentry/react'

declare global {
    interface Window {
        gtag: (...args: any[]) => void
        dataLayer: any[]
    }
}

// GTM allowed partner IDs - parsed from env var (comma-separated) or default
const GTM_ALLOWED_PARTNER_IDS: string[] = import.meta.env.VITE_GTM_ALLOWED_PARTNER_IDS
    ? (import.meta.env.VITE_GTM_ALLOWED_PARTNER_IDS as string).split(',').map((id) => id.trim())
    : ['001Dn00000HrdDO']

/**
 * Check if GTM events are enabled for a given partner ID.
 * Returns true if the partner ID is in the allowlist.
 */
export const isGTMEnabledForPartner = (partnerId?: string | null): boolean => {
    if (!partnerId) return false
    return GTM_ALLOWED_PARTNER_IDS.includes(partnerId)
}

interface GTMEventParams {
    event: string
    partner_id: string
    offer_id?: string
    application_id?: string
    [key: string]: any
}

export type FigGtmOfferClassification = 'fig_elite' | 'fig_core' | 'kizzo'

/** GTM segment: Kizzo from `is_kizzo` only; Fig Elite from backend super-prime flag (`PersonalLoanOffer.is_super_prime`). */
export const getFigGtmOfferClassification = (isKizzo: boolean | null | undefined, isSuperPrime: boolean | null | undefined): FigGtmOfferClassification => {
    if (isKizzo === true) {
        return 'kizzo'
    }
    if (isSuperPrime === true) {
        return 'fig_elite'
    }
    return 'fig_core'
}

/** Prequal offer pages: fields required to fire `offer_approved` on the data layer. */
export type GtmPrequalOfferApprovedSource = {
    offer_id?: string | null
    is_kizzo?: boolean | null
    is_super_prime?: boolean | null
}

/** Affiliate / embedded prequal approved step — dedupes by `offer_id`. */
export const pushGtmPrequalOfferApproved = (args: {
    success: GtmPrequalOfferApprovedSource | null | undefined
    applicationId: string | undefined
    partnerId: string
    maxLoanAmount: number
    apr: number
}): void => {
    const { success, applicationId, partnerId, maxLoanAmount, apr } = args
    const offerId = success?.offer_id
    if (!offerId || !partnerId) {
        return
    }
    pushGTMEvent(
        {
            event: 'offer_approved',
            offer_id: offerId,
            application_id: applicationId,
            partner_id: partnerId,
            max_loan_amount: maxLoanAmount,
            apr,
            fig_offer_classification: getFigGtmOfferClassification(success?.is_kizzo, success?.is_super_prime)
        },
        offerId
    )
}

/** Origination review / funded — dedupes by `application_id`. */
export type GtmOfferBookedSource = {
    offer_id?: string | null
    apr?: string | null
    is_kizzo?: boolean | null
    is_super_prime?: boolean | null
}

export const pushGtmOfferBooked = (args: {
    applicationId: string | undefined
    partnerId: string | undefined
    offer: GtmOfferBookedSource | null | undefined
    loanAmount?: number | null
    loanTerm?: number | null
}): void => {
    const { applicationId, partnerId, offer, loanAmount, loanTerm } = args
    if (!applicationId || !partnerId || !offer?.offer_id) {
        return
    }
    pushGTMEvent(
        {
            event: 'offer_booked',
            offer_id: offer.offer_id,
            application_id: applicationId,
            partner_id: partnerId,
            loan_amount: loanAmount,
            loan_term: loanTerm,
            apr: offer.apr,
            fig_offer_classification: getFigGtmOfferClassification(offer.is_kizzo, offer.is_super_prime)
        },
        applicationId
    )
}

/**
 * Push an event to the GTM dataLayer with localStorage-based deduplication.
 * Only fires for allowed partner IDs (configured via VITE_GTM_ALLOWED_PARTNER_IDS).
 *
 * @param params - The event data to push (must include 'event' name, should include 'partner_id')
 * @param uniqueKey - A unique identifier (e.g., offer_id) to prevent duplicate events
 *
 * How deduplication works:
 * - We store a key like "gtm_event_offer_approved_OFFER123" in localStorage
 * - If that key exists, the event has already been fired for this offer, so we skip
 * - This persists across page refreshes, browser back/forward, and return visits
 */
export const pushGTMEvent = (params: GTMEventParams, uniqueKey?: string) => {
    // Check if GTM events are enabled for this partner
    if (!isGTMEnabledForPartner(params.partner_id)) {
        return
    }

    const storageKey = uniqueKey ? `gtm_event_${params.event}_${uniqueKey}` : `gtm_event_${params.event}`

    try {
        // Check if we've already fired this event for this unique key
        if (localStorage.getItem(storageKey)) {
            return // Already fired - skip to prevent double counting
        }

        // Push to dataLayer
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(params)

        // Mark as fired
        localStorage.setItem(storageKey, 'true')
    } catch {
        // Fallback for private browsing or localStorage unavailable
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(params)
    }
}

interface TrackGA4EventParams {
    event_category: string
    event_name: string
    event_label: string
    event_location: string
}

// Internal function to actually send the event to Google Analytics
const sendGA4Event = (eventName: string, parameters?: TrackGA4EventParams) => {
    if (!window.gtag) {
        captureMessage('Google Analytics not initialized. Event tracking disabled.', {
            level: 'warning',
            extra: {
                eventName
            }
        })
        return
    }

    try {
        window.gtag('event', eventName, parameters)
    } catch {
        captureMessage('Failed to track GA4 event:', {
            level: 'warning',
            extra: {
                eventName
            }
        })
    }
}

/**
 * Generic function to track GA4 events with session-based deduplication.
 * Tracks an event only once per session using sessionStorage.
 *
 * @param eventName - The name of the event to track
 * @param parameters - Optional event parameters
 * @param storageKeyPrefix - Prefix for the sessionStorage key (e.g., 'ga_event', 'ga_page_view')
 */
const trackGA4EventOnce = (eventName: string, parameters: TrackGA4EventParams | undefined, storageKeyPrefix: string) => {
    // Use sessionStorage to track which events have been sent in this session
    const storageKey = `${storageKeyPrefix}_${eventName}`

    // Check if this event has already been tracked in this session
    try {
        if (sessionStorage.getItem(storageKey)) {
            return
        }

        // Track the event
        sendGA4Event(eventName, parameters)

        // Mark as tracked for this session
        sessionStorage.setItem(storageKey, 'true')
    } catch {
        // If sessionStorage is not available (e.g., private browsing), fall back to regular tracking
        sendGA4Event(eventName, parameters)
    }
}

// Track GA4 custom event (only once per session per event)
export const trackGA4Event = (eventName: string, parameters?: TrackGA4EventParams) => {
    trackGA4EventOnce(eventName, parameters, 'ga_event')
}

// Track GA4 page view event (only once per session per page)
export const trackGA4PageView = (eventName: string, parameters?: TrackGA4EventParams) => {
    trackGA4EventOnce(eventName, parameters, 'ga_page_view')
}

// Flag to track if Google Analytics has been initialized
let isGAInitialized = false

// Set up event delegation to automatically track elements with data-ga-* attributes
const setupGAEventDelegation = () => {
    // Use a named function so we can reference it for removal if needed
    const handleGAClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement
        const element = target.closest('[data-ga-event]') as HTMLElement

        if (!element) {
            return
        }

        const eventName = element.getAttribute('data-ga-event')
        if (!eventName) {
            return
        }

        const parameters: TrackGA4EventParams = {
            event_category: 'user_interaction',
            event_name: eventName,
            event_label: element.textContent?.trim() || eventName,
            event_location: element.getAttribute('data-ga-param-event-location') || 'unknown'
        }

        // Extract all data-ga-param-* attributes
        Array.from(element.attributes).forEach((attr) => {
            if (attr.name.startsWith('data-ga-param-')) {
                const paramName = attr.name.replace('data-ga-param-', '')
                parameters[paramName as keyof TrackGA4EventParams] = attr.value
            }
        })

        // Add default parameters if not specified
        if (!parameters.event_category) {
            parameters.event_category = 'user_interaction'
        }

        if (!parameters.event_label) {
            parameters.event_label = element.textContent?.trim() || element.getAttribute('data-ga-event') || ''
        }

        trackGA4Event(eventName, parameters)
    }

    document.addEventListener('click', handleGAClick)
}

// Initialize Google Analytics 4 tracking
export const initializeGoogleAnalytics = () => {
    // Prevent multiple initializations
    if (isGAInitialized) {
        return
    }

    // GA is now loaded via HTML, just set up event delegation
    setupGAEventDelegation()
    isGAInitialized = true
}
